import { MenuItem, Typography } from '@material-ui/core';
import ConsumerRoutePath from 'app/consumer-app/ConsumerRoutePath';
import { useAssignedProduct } from 'app/core/api/product-assignment/api-product-assignment';
import { productImg } from 'app/core/api/product-assignment/api-product-assignment.types';
import {
  useAssignmentOpenRepair,
  useCreateDefect,
} from 'app/core/api/repairer/api-repairer';
import { MissingParamError } from 'app/core/errors/error-variants';
import ProductPictureOrDefault from 'app/shared/components/app-image/ProductPictureOrDefault';
import Button from 'app/shared/components/button/Button';
import FormSelectInput from 'app/shared/components/form-input/FormSelectInput';
import FormTextInput from 'app/shared/components/form-input/FormTextInput';
import useInputForm from 'app/shared/components/form-input/utilities/use-input-form';
import { InputTitle } from 'app/shared/components/misc';
import Subheading from 'app/shared/components/typography/subheading/Subheading';
import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
import Form from 'app/shared/frames/Form';
import SharedRoutePath from 'app/shared/SharedRoutePath';
import ClassName from 'app/styles/ClassName';
import styledFuncs from 'app/styles/styledFuncs';
import { useUrlParams } from 'app/tools/hooks/misc-hooks';
import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components/macro';
import { DefectTypes, DefectTypesMap } from './report-fault-utils';

const ProductDetail = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const ProductImage = styled(ProductPictureOrDefault)`
  flex: 1 0 40%;
  padding: 0;
  margin-left: ${styledFuncs.pad()};
`;

type FormValues = {
  title: string;
  description: string;
};

export default function ReportFault(): JSX.Element {
  const assignmentId = +useUrlParams(['id']).id;
  const history = useHistory();
  const showInfo = useShowInfo();

  const { resource: entry, loadingMissing: entryLoadingMissing } =
    useAssignedProduct(assignmentId);
  const {
    request$: createNewDefect,
    sending: loading,
    error: createDefectError,
  } = useCreateDefect();
  const { resource: openRepairCaseId, loading: loadingOpenCase } =
    useAssignmentOpenRepair(assignmentId);

  const { handleSubmit, inputProps } = useInputForm<FormValues>({
    apiError: createDefectError,
  });

  useEffect(() => {
    if (openRepairCaseId) {
      history.replace(`${SharedRoutePath.CHAT_LIST}/all`);
      history.push(`${SharedRoutePath.CHAT}/${openRepairCaseId}`);
    }
  }, [history, assignmentId, openRepairCaseId]);

  if (entryLoadingMissing || loadingOpenCase) {
    // TODO: add loading screen and timeout here if necessary
    return <></>;
  }

  if (!assignmentId) {
    throw new MissingParamError('entry id');
  }
  const { productDetails } = entry || {};
  const submit = (formValues: FormValues) => {
    createNewDefect({
      ...formValues,
      assignedProduct: assignmentId,
    }).subscribe({
      next: createdDefect => {
        history.push(
          `${ConsumerRoutePath.PRODUCT_SEARCH_REPAIRER}/${
            assignmentId || ''
          }`,
          createdDefect
        );
      },
      error: () => {
        showInfo.showSnack({
          message: 'Something went wrong!! please try again.',
        });
      },
    });
  };
  return (
    <>
      <ProductDetail>
        <Subheading centered={false}>
          {productDetails?.name}
        </Subheading>
        <ProductImage
          src={productImg(entry)}
          alt={productDetails?.name || ''}
          productId={productDetails?.id}
          height="180px"
        />
      </ProductDetail>
      <Form onSubmit={handleSubmit(submit)}>
        <InputTitle>Select issue</InputTitle>
        <FormSelectInput
          classicPlaceholder
          controlName="title"
          displayEmpty
          rules={{
            required: 'Please select a defect type',
          }}
          {...inputProps}
        >
          <MenuItem value="" disabled divider>
            <Typography color="primary">Choose an option</Typography>
          </MenuItem>
          {Object.keys(DefectTypes).map(defectType => (
            <MenuItem value={defectType} key={defectType} divider>
              {DefectTypesMap.get(defectType)}
            </MenuItem>
          ))}
        </FormSelectInput>
        <InputTitle>Enter any additional details</InputTitle>
        <FormTextInput
          controlName="description"
          placeholder="Type your description here..."
          classicPlaceholder
          multiline
          rules={{
            required: 'Add any relevant details to help us out!',
          }}
          {...inputProps}
        />
        <Button type="submit" pending={loading}>
          Find a repairer
        </Button>
      </Form>
      <Button
        navBackAction
        variant="text"
        className={ClassName.NO_HEIGHT_MARGIN}
        css="margin-top: -4px;"
      >
        Cancel
      </Button>
    </>
  );
}
